import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Category } from '../types/category';
import { ExtendedMarkerData } from '../types/marker';

interface AppState {
  markers: ExtendedMarkerData[];
  categories: Category[];
  customActionTypes: string[];
  selectedMarkerId: string | null;
  visibleMarkerIds: string[];
  isLoading: boolean;
  error: string | null;
  visibleCategories: Set<string>;
  selectedCategory: Category | null;
  actionTypes: Array<{ value: string; name: string }>;
  showOnlyMarkersWithNotifications: boolean;
}

interface AppAction {
  type: 
    | 'SET_MARKERS' 
    | 'ADD_MARKER' 
    | 'UPDATE_MARKER' 
    | 'DELETE_MARKER' 
    | 'SET_CATEGORIES' 
    | 'SET_SELECTED_MARKER' 
    | 'SET_VISIBLE_MARKERS'
    | 'SET_CUSTOM_ACTION_TYPES'
    | 'ADD_CUSTOM_ACTION_TYPE'
    | 'SET_LOADING'
    | 'SET_ERROR'
    | 'SET_VISIBLE_CATEGORIES'
    | 'SELECT_CATEGORY'
    | 'TOGGLE_CATEGORY_VISIBILITY'
    | 'DELETE_CATEGORY'
    | 'ADD_ACTION_TYPE'
    | 'TOGGLE_SHOW_ONLY_MARKERS_WITH_NOTIFICATIONS';
  payload: any;
}

const initialState: AppState = {
  markers: [],
  categories: [],
  customActionTypes: [],
  selectedMarkerId: null,
  visibleMarkerIds: [],
  isLoading: false,
  error: null,
  visibleCategories: new Set(),
  selectedCategory: null,
  actionTypes: [
    { value: 'call', name: 'Appel' },
    { value: 'visit', name: 'Visite' },
    { value: 'email', name: 'Email' },
    { value: 'other', name: 'Autre' }
  ],
  showOnlyMarkersWithNotifications: false
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | undefined>(undefined);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_MARKERS':
      return { ...state, markers: action.payload };
    case 'ADD_MARKER': {
      const newMarker = action.payload;
      // S'assurer que la catégorie du nouveau marker est visible
      const updatedVisibleCategories = new Set(state.visibleCategories);
      if (newMarker.categoryId) {
        updatedVisibleCategories.add(newMarker.categoryId);
      }
      return {
        ...state,
        markers: [...state.markers, newMarker],
        visibleCategories: updatedVisibleCategories
      };
    }
    case 'UPDATE_MARKER':
      return {
        ...state,
        markers: state.markers.map(marker =>
          marker.id === action.payload.id 
            ? { ...marker, ...action.payload.data } 
            : marker
        ),
      };
    case 'DELETE_MARKER':
      return {
        ...state,
        markers: state.markers.filter(marker => marker.id !== action.payload),
      };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_SELECTED_MARKER':
      return { ...state, selectedMarkerId: action.payload };
    case 'SET_VISIBLE_MARKERS':
      return { ...state, visibleMarkerIds: action.payload };
    case 'SET_CUSTOM_ACTION_TYPES':
      return { ...state, customActionTypes: action.payload };
    case 'ADD_CUSTOM_ACTION_TYPE':
      if (!state.customActionTypes.includes(action.payload)) {
        return { 
          ...state, 
          customActionTypes: [...state.customActionTypes, action.payload]
        };
      }
      return state;
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_VISIBLE_CATEGORIES':
      return { ...state, visibleCategories: action.payload };
    case 'SELECT_CATEGORY':
      return { ...state, selectedCategory: action.payload };
    case 'TOGGLE_CATEGORY_VISIBILITY':
      const _categoryIdToToggle = action.payload;
      const newVisibleCategories = new Set(state.visibleCategories);
      if (newVisibleCategories.has(action.payload)) {
        newVisibleCategories.delete(action.payload);
      } else {
        newVisibleCategories.add(action.payload);
      }

      return {
        ...state,
        visibleCategories: newVisibleCategories
      };
    case 'DELETE_CATEGORY':
      const { categoryId, deletedMarkerIds } = action.payload;
      
      // Filtrer les marqueurs pour supprimer ceux qui ont été supprimés de la base de données
      const markersAfterDelete = state.markers.filter(marker => 
        !deletedMarkerIds.includes(marker.id) && marker.categoryId !== categoryId
      );
      const updatedCategories = state.categories.filter(cat => cat.id !== categoryId);
      const updatedVisibleCategories = new Set(
        Array.from(state.visibleCategories).filter(id => id !== categoryId)
      );

      return {
        ...state,
        categories: updatedCategories,
        markers: markersAfterDelete,
        visibleCategories: updatedVisibleCategories
      };
    case 'ADD_ACTION_TYPE':
      return { ...state, actionTypes: [...state.actionTypes, action.payload] };
    case 'TOGGLE_SHOW_ONLY_MARKERS_WITH_NOTIFICATIONS':
      return { ...state, showOnlyMarkersWithNotifications: !state.showOnlyMarkersWithNotifications };
    default:
      return state;
  }
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Note: Le useEffect qui forçait toutes les catégories à être visibles a été supprimé
  // car la visibilité est maintenant gérée en base de données via la colonne is_visible

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}

